import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Download, Search, MoreVertical, Pencil, UserMinus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_BADGE = {
  Working: "bg-green-100 text-green-800 border-green-200",
  Left: "bg-red-100 text-red-800 border-red-200",
  Rejoin: "bg-blue-100 text-blue-800 border-blue-200",
  "Long Holiday": "bg-orange-100 text-orange-800 border-orange-200",
};

export default function EmployeesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ unit_id: "", department_id: "", status: "", category: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [pendingOnly, setPendingOnly] = useState(false);

  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [emp, u, d] = await Promise.all([
        api.get("/employees", { params }),
        api.get("/units"),
        api.get("/departments"),
      ]);
      setEmployees(emp.data);
      setUnits(u.data);
      setDepartments(d.data);
    } catch (e) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const filteredDepts = useMemo(() => {
    if (!filters.unit_id) return departments;
    return departments.filter((d) => d.unit_id === filters.unit_id);
  }, [departments, filters.unit_id]);

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";
  const unitName = (id) => units.find((u) => u.id === id)?.name || "—";

  const isPendingRow = (e) => e.approval_status === "PENDING_APPROVAL" || !!e.pending_edit_request_id;
  const pendingCount = useMemo(() => employees.filter(isPendingRow).length, [employees]);
  const visibleEmployees = useMemo(
    () => pendingOnly ? employees.filter(isPendingRow) : employees,
    [employees, pendingOnly],
  );

  const toggleSelect = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      ns.has(id) ? ns.delete(id) : ns.add(id);
      return ns;
    });
  };

  const exportCsv = () => {
    if (!visibleEmployees.length) return toast.info("No employees to export");
    const headers = ["Punch ID", "Code", "Bio Punch", "Name", "Unit", "Department", "Status", "CTC", "Aadhaar"];
    const rows = visibleEmployees.map((e) => [
      e.punch_id,
      e.employee_code,
      e.biometric_id || "",
      e.name,
      unitName(e.unit_id),
      deptName(e.department_id),
      e.status,
      e.ctc,
      e.aadhaar_masked || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employees_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkMarkLeft = async () => {
    if (!selected.size) return toast.info("Select employees first");
    if (!window.confirm(`Mark ${selected.size} employee(s) as Left?`)) return;
    try {
      await api.post("/employees/bulk-mark-left", Array.from(selected));
      toast.success(`Marked ${selected.size} as Left`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  // Phase 1D — generic bulk update (designation / category / department)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState("category");
  const [bulkValue, setBulkValue] = useState("");
  const runBulkUpdate = async () => {
    if (!selected.size) return toast.info("Select employees first");
    if (!bulkField || bulkValue === "") return toast.error("Pick a field and value first");
    try {
      const res = await api.post("/employees/bulk-update", {
        employee_ids: Array.from(selected),
        updates: { [bulkField]: bulkValue },
      });
      toast.success(`Updated ${res.data.modified} of ${res.data.matched} employees`);
      setBulkOpen(false);
      setBulkValue("");
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    }
  };

  return (
    <div data-testid="employees-list-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Master data</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Employees
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {loading ? "Loading..." : `${visibleEmployees.length} record${visibleEmployees.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="export-csv-btn" onClick={exportCsv}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {canEdit && (
            <>
              <Link to="/employees/bulk-upload">
                <Button variant="outline" data-testid="bulk-upload-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Upload
                </Button>
              </Link>
              <Link to="/employees/new">
                <Button data-testid="add-employee-btn" className="brand-bg brand-bg-hover text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="search-input"
            placeholder="Search name, punch ID, code..."
            className="pl-9"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
        </div>
        <Select
          value={filters.unit_id || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, unit_id: v === "all" ? "" : v, department_id: "" }))}
        >
          <SelectTrigger data-testid="filter-unit"><SelectValue placeholder="All units" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All units</SelectItem>
            {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={filters.department_id || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, department_id: v === "all" ? "" : v }))}
        >
          <SelectTrigger data-testid="filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {filteredDepts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={filters.status || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}
        >
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Working">Working</SelectItem>
            <SelectItem value="Left">Left</SelectItem>
            <SelectItem value="Rejoin">Rejoin</SelectItem>
            <SelectItem value="Long Holiday">Long Holiday</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Quick filter chips */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-2">
          <button
            data-testid="pending-revision-filter-chip"
            onClick={() => setPendingOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              pendingOnly
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${pendingOnly ? "bg-white" : "bg-amber-500"}`} />
            Pending Revision ({pendingCount})
            {pendingOnly && <span className="ml-1">✕</span>}
          </button>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && canEdit && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between bg-red-50 border border-red-200 rounded-md px-4 py-2"
        >
          <div className="text-sm text-red-900 font-medium">
            {selected.size} selected
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="bulk-update-btn"
                    onClick={() => setBulkOpen(true)}>
              Bulk Update Field
            </Button>
            <Button size="sm" variant="outline" data-testid="bulk-mark-left-btn" onClick={bulkMarkLeft}>
              <UserMinus className="h-4 w-4 mr-2" />
              Mark as Left
            </Button>
          </div>
        </div>
      )}

      {/* Bulk-update dialog */}
      {bulkOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setBulkOpen(false)}
        >
          <div
            data-testid="bulk-update-dialog"
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              Bulk Update — {selected.size} selected
            </div>
            <h3 className="text-lg font-semibold mb-4">Set a field for selected employees</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Field</label>
                <select
                  value={bulkField}
                  onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }}
                  className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="bulk-field-select"
                >
                  <option value="category">Category</option>
                  <option value="designation">Designation</option>
                  <option value="department_id">Department</option>
                  <option value="status">Status</option>
                  <option value="overtime_applicable">Overtime applicable</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">New value</label>
                {bulkField === "category" ? (
                  <select
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    data-testid="bulk-value-input"
                  >
                    <option value="">Choose...</option>
                    <option value="white_collar">White-Collar</option>
                    <option value="blue_collar">Blue-Collar</option>
                    <option value="driver">Driver / Field Staff</option>
                    <option value="manual_entry">Manual Entry</option>
                  </select>
                ) : bulkField === "status" ? (
                  <select
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    data-testid="bulk-value-input"
                  >
                    <option value="">Choose...</option>
                    <option value="Working">Working</option>
                    <option value="On Leave">On Leave</option>
                    <option value="Left">Left</option>
                    <option value="Terminated">Terminated</option>
                  </select>
                ) : bulkField === "department_id" ? (
                  <select
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    data-testid="bulk-value-input"
                  >
                    <option value="">Choose...</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({unitName(d.unit_id)})</option>
                    ))}
                  </select>
                ) : bulkField === "overtime_applicable" ? (
                  <select
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value === "true")}
                    className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    data-testid="bulk-value-input"
                  >
                    <option value="">Choose...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full mt-1.5 border border-slate-200 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. Floor Supervisor"
                    data-testid="bulk-value-input"
                  />
                )}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button onClick={runBulkUpdate} className="brand-bg brand-bg-hover text-white"
                      data-testid="bulk-confirm-btn">
                Update {selected.size}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                {canEdit && <th className="px-4 py-3 w-10"></th>}
                <th className="px-4 py-3">Punch ID</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3" title="Mantra device numeric user ID (real-time biometric punches)">Bio Punch</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Unit / Department</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">CTC (Monthly)</th>
                <th className="px-4 py-3">Aadhaar</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.length === 0 && !loading && (
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="px-4 py-12 text-center text-slate-500">
                    No employees yet. {canEdit && (
                      <Link to="/employees/new" className="brand-text font-medium underline">
                        Add your first employee
                      </Link>
                    )}
                  </td>
                </tr>
              )}
              {visibleEmployees.map((e) => {
                const isPendingNew = e.approval_status === "PENDING_APPROVAL";
                const isPendingEdit = !!e.pending_edit_request_id;
                const isPending = isPendingNew || isPendingEdit;
                return (
                <tr
                  key={e.id}
                  className={`transition-colors ${isPending ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400" : "hover:bg-slate-50"}`}
                  data-testid={isPending ? `employee-row-pending-${e.id}` : `employee-row-${e.id}`}
                >
                  {canEdit && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`row-check-${e.punch_id}`}
                        checked={selected.has(e.id)}
                        onChange={() => toggleSelect(e.id)}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{e.punch_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{e.employee_code}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {e.biometric_id ? (
                      <span className="text-emerald-700 font-semibold">{e.biometric_id}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {e.name}
                    {isPending && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-amber-200 text-amber-900 border border-amber-300"
                        data-testid={`pending-badge-${e.id}`}
                        title={isPendingNew
                          ? "New employee — awaiting Super Admin approval"
                          : "Sensitive fields (e.g. salary) changed — awaiting Super Admin approval"}
                      >
                        {isPendingNew ? "New — Pending" : "Revision Pending"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="text-xs">{unitName(e.unit_id)}</div>
                    <div className="text-[11px] text-slate-500">{deptName(e.department_id)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`${STATUS_BADGE[e.status] || ""} border`}>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">
                    {formatINR(e.ctc)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">
                    {e.aadhaar_masked || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`row-actions-${e.punch_id}`}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          <MoreVertical className="h-4 w-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/employees/${e.id}`)}>
                          <Pencil className="h-4 w-4 mr-2" /> {canEdit ? "Edit" : "View"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
